import { IncomingCall } from '@azure/communication-calling';
import { LoggingService } from '../services/logging.service';
import { Utilities } from '../utils/utilities';
import { CallerIdentity } from '../enums/caller-identity';
import { TeamInfo } from './team-info';
import { Channel, Modality } from './task-types.model';

export class ConversationInfo {
  public id: string;
  public resolvedParameters: object;
  public customContextParameters?: object;
  public isPstn: boolean;
  public isInternal: boolean;

  public isExternal: boolean;
  public isServiceCall: boolean;
  public isDirectTeamsCall: boolean;
  public isSupervision?: boolean;
  public isUserTransferredCall?: boolean;
  public isExternalTaskSession: boolean;
  public isImSession: boolean;

  public modality: Modality;
  public channel: Channel;

  public caller: string; // for chat it's microsoftTeamsUserId
  public callerDisplayName: string;

  // Caller identity used to resolve $(Caller.TelNumber)/$(Caller.+TelNumber)/$(Caller.Id) placeholders.
  public phoneNumber?: string;
  public microsoftTeamsUserId?: string;

  public teamInfo?: TeamInfo;

  // will be rechecked later with transfer chat
  public isOutboundServiceCall: boolean;
  public isServiceTransferredCall: boolean;
  public initialServiceName?: string;
  public callId?: string;
}

export class IncomingCallInfo extends ConversationInfo {
  public callType: CallerIdentity;
  public teamsUserId: string;
  public phoneNumber: string;

  public caller: string;
  public teamInfo?: TeamInfo; // details for current service

  public isServiceTransferredCall: boolean;

  public microsoftTeamsUserId: string; // id of the user
  public serviceCallApplicationId: string;

  public constructor(
    incomingCall: IncomingCall,
    private readonly loggingService: LoggingService,
  ) {
    super();
    if (!incomingCall) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const callInternal = (incomingCall as any)._callInternal || (incomingCall as any)._call || (incomingCall as any)._callCommon;
    if (!callInternal) {
      loggingService.Error(`Internal call info not found in ACS!`);
    }
    const callerIdentity = callInternal?._callerIdentity || {};

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const remoteParticipants = callInternal?._remoteParticipants as Array<any>;

    const phoneCaller = remoteParticipants.find((x) => x?._identifier?.kind === 'phoneNumber');
    const teamsCaller = remoteParticipants.find(
      // for supervision case will need additional filter '!== currentUser.microsoftTeamsUserId'
      (x) => x?._identifier?.kind === 'microsoftTeamsUser' && Utilities.isValidUuid(x._identifier.microsoftTeamsUserId),
    );

    this.id = incomingCall.id;
    this.callType = (callerIdentity.kind || CallerIdentity.unknown) as CallerIdentity;
    this.isPstn = this.callType === CallerIdentity.phoneNumber || !!phoneCaller;
    this.isDirectTeamsCall =
      (this.callType === CallerIdentity.microsoftTeamsUser && Utilities.isValidUuid(callerIdentity.microsoftTeamsUserId)) ||
      (!!teamsCaller && remoteParticipants?.length === 1);

    this.isSupervision = !!remoteParticipants.find(
      (x) => x?._identifier?.rawId?.includes('teamsvisitor') && x?._displayName?.includes('supervision'),
    );

    this.isUserTransferredCall = !!remoteParticipants.find(
      (x) => x?._identifier?.rawId?.includes('teamsvisitor') && x?._displayName?.includes('transferred by'),
    );

    this.setCallerId(remoteParticipants, teamsCaller, phoneCaller);
  }

  public set callerInternal(callerInternal: boolean) {
    this.isInternal = !!callerInternal;
    this.isExternal = !this.isInternal;
  }

  public static acceptedByUserRawId(incomingCall: IncomingCall): string {
    const callRegistries = (incomingCall as any)?._callCommon?._callAgent?._callClient?._callStack?._createStackBaseWaitPromise
      ?.__zone_symbol__value?.callRegistries;
    if (!callRegistries?.length) {
      return null;
    }
    const calls = callRegistries[0]?.calls;
    if (!calls?.length) {
      return null;
    }
    return calls[0]?.acceptedElsewhereBy?.id;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private setCallerId(remoteParticipants: any[], teamsCaller: any, phoneCaller: any): void {
    try {
      // teams caller id
      if (teamsCaller) {
        this.microsoftTeamsUserId = teamsCaller._identifier.microsoftTeamsUserId;
        this.callerDisplayName = teamsCaller._displayName;
      }

      // phone caller number
      if (phoneCaller) {
        this.phoneNumber = phoneCaller._identifier.phoneNumber;
      }

      // for supervision case will need phone first
      this.caller = this.microsoftTeamsUserId || this.phoneNumber;

      // applicationId of service where call initially comes
      const serviceParticipant = remoteParticipants.find(
        (x) => x._identifier?.kind === CallerIdentity.microsoftBot || x._identifier?.kind === CallerIdentity.microsoftTeamsApp,
      );
      if (serviceParticipant) {
        this.serviceCallApplicationId = serviceParticipant._identifier.botId || serviceParticipant._identifier.teamsAppId;
      }

      const isDirectPSTN = this.isPstn && remoteParticipants?.length === 1;
      this.isServiceCall = !this.isDirectTeamsCall && !isDirectPSTN && !!this.serviceCallApplicationId;

      if (this.isServiceCall) {
        const serviceParticipantState = serviceParticipant?._state?.toLowerCase();
        const isOutbound = remoteParticipants.length === 1 && serviceParticipantState === 'ringing';
        this.isOutboundServiceCall = isOutbound;
      }
    } catch (err) {
      this.loggingService.Error(`Error setting caller: ${JSON.stringify(err)}`);
    }
  }
}
